import {
  ApartmentOutlined,
  SearchOutlined,
  TeamOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Checkbox,
  Empty,
  Input,
  List,
  Modal,
  Space,
  Spin,
  Tag,
  Tree,
  Typography,
} from 'antd';
import React from 'react';
import type { PersonnelMember } from '../PersonnelMemberSelect';

export type PersonnelDirectoryNode = {
  key: string;
  title: React.ReactNode;
  children?: PersonnelDirectoryNode[];
};

type PersonnelDirectoryPickerProps = {
  members: PersonnelMember[];
  departments?: PersonnelDirectoryNode[];
  value?: string[];
  onChange?: (value: string[]) => void;
  loadDepartmentMembers?: (departmentId: string) => Promise<PersonnelMember[]>;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  modalTitle?: string;
};

export const PersonnelDirectoryPicker: React.FC<
  PersonnelDirectoryPickerProps
> = ({
  members,
  departments = [],
  value = [],
  onChange,
  loadDepartmentMembers,
  loading,
  disabled,
  placeholder = '选择成员',
  modalTitle = '选择成员',
}) => {
  const [open, setOpen] = React.useState(false);
  const [keyword, setKeyword] = React.useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] =
    React.useState<string>();
  const [directoryMembers, setDirectoryMembers] = React.useState(members);
  const [departmentLoading, setDepartmentLoading] = React.useState(false);

  React.useEffect(() => {
    if (!selectedDepartmentId) {
      setDirectoryMembers(members);
    }
  }, [members, selectedDepartmentId]);

  const availableMembers = React.useMemo(() => {
    const map = new Map<string, PersonnelMember>();
    [...members, ...directoryMembers].forEach((member) => {
      map.set(member.memberUuid, member);
    });
    return Array.from(map.values());
  }, [directoryMembers, members]);

  const selectedMembers = React.useMemo(
    () =>
      value.map((memberUuid) => ({
        memberUuid,
        name:
          availableMembers.find((member) => member.memberUuid === memberUuid)
            ?.name || memberUuid,
      })),
    [availableMembers, value],
  );

  const filteredMembers = React.useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    if (!normalizedKeyword) {
      return directoryMembers;
    }
    return directoryMembers.filter((member) =>
      [member.name, member.organizationName, member.email, member.phone]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(normalizedKeyword),
    );
  }, [directoryMembers, keyword]);

  const toggleMember = (memberUuid: string) => {
    onChange?.(
      value.includes(memberUuid)
        ? value.filter((item) => item !== memberUuid)
        : [...value, memberUuid],
    );
  };

  const selectDepartment = async (departmentId?: string) => {
    setSelectedDepartmentId(departmentId);
    setKeyword('');
    if (!departmentId || !loadDepartmentMembers) {
      setDirectoryMembers(members);
      return;
    }

    setDepartmentLoading(true);
    try {
      setDirectoryMembers(await loadDepartmentMembers(departmentId));
    } finally {
      setDepartmentLoading(false);
    }
  };

  return (
    <>
      <div className="personnel-directory-picker">
        <Button
          block
          className="personnel-directory-picker__trigger"
          disabled={disabled}
          icon={<UserAddOutlined />}
          loading={loading}
          type="text"
          onClick={() => setOpen(true)}
        >
          {placeholder}
        </Button>
        {selectedMembers.length ? (
          <Space
            className="personnel-directory-picker__tags"
            wrap
            size={[4, 4]}
          >
            {selectedMembers.map((member) => (
              <Tag
                key={member.memberUuid}
                closable={!disabled}
                onClose={(event) => {
                  event.preventDefault();
                  toggleMember(member.memberUuid);
                }}
              >
                {member.name}
              </Tag>
            ))}
          </Space>
        ) : null}
      </div>

      <Modal
        title={modalTitle}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => setOpen(false)}
        okText="确定"
        width="min(920px, calc(100vw - 48px))"
        styles={{ body: { minHeight: 460 } }}
      >
        <div className="personnel-directory-picker__layout">
          <div className="personnel-directory-picker__tree">
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Button
                icon={<ApartmentOutlined />}
                type={!selectedDepartmentId ? 'primary' : 'text'}
                onClick={() => void selectDepartment()}
              >
                全部部门
              </Button>
              <Tree
                blockNode
                showLine
                selectedKeys={
                  selectedDepartmentId ? [selectedDepartmentId] : []
                }
                treeData={departments}
                onSelect={(keys) =>
                  void selectDepartment(String(keys[0] || ''))
                }
              />
            </Space>
          </div>

          <div className="personnel-directory-picker__members">
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="搜索姓名、部门、邮箱或手机号"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
              />
              {selectedMembers.length ? (
                <Space wrap size={[4, 4]}>
                  {selectedMembers.map((member) => (
                    <Tag
                      key={member.memberUuid}
                      closable
                      onClose={(event) => {
                        event.preventDefault();
                        toggleMember(member.memberUuid);
                      }}
                    >
                      {member.name}
                    </Tag>
                  ))}
                </Space>
              ) : null}
              {departmentLoading ? (
                <div className="personnel-directory-picker__loading">
                  <Spin />
                </div>
              ) : filteredMembers.length ? (
                <List
                  dataSource={filteredMembers}
                  renderItem={(member) => (
                    <List.Item
                      className="personnel-directory-picker__member"
                      onClick={() => toggleMember(member.memberUuid)}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            src={member.avatarUrl}
                            icon={<TeamOutlined />}
                          >
                            {member.name.slice(0, 1)}
                          </Avatar>
                        }
                        title={member.name}
                        description={
                          <Typography.Text type="secondary">
                            {member.organizationName ||
                              member.email ||
                              member.phone ||
                              '-'}
                          </Typography.Text>
                        }
                      />
                      <Checkbox
                        checked={value.includes(member.memberUuid)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggleMember(member.memberUuid)}
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Space>
          </div>
        </div>
      </Modal>
    </>
  );
};
